import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiFilter, FiChevronDown } from 'react-icons/fi';
import SearchFilters from '../../components/SearchFilters/SearchFilters';
import VideoCard from '../../components/VideoCard/VideoCard';
import { useSearch, SortBy } from '../../contexts/SearchContext';
import { search, saveSearchHistory, SearchResults } from '../../services/searchService';
import './SearchPage.css';

/// SearchPageコンポーネント
const SearchPage: React.FC = () => {
  const { state, dispatch } = useSearch();
  const [results, setResults] = useState<SearchResults>({ videos: [], totalCount: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSearchKey, setLastSearchKey] = useState('');

  /// 検索実行
  const executeSearch = useCallback(async () => {
    // 同一条件の連続検索を抑制
    const searchKey = JSON.stringify({
      k: state.keyword,
      c: state.category,
      d: state.dateRange,
      s: state.sortBy
    });
    
    if (searchKey === lastSearchKey) {
      return;
    }
    
    setLastSearchKey(searchKey);
    setIsLoading(true);
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const searchResults = await search({
        keyword: state.keyword,
        category: state.category,
        dateRange: state.dateRange,
        sortBy: state.sortBy
      });

      setResults(searchResults);

      // 検索履歴を保存
      if (state.keyword.trim()) {
        saveSearchHistory(state.keyword);
      }
    } catch (error) {
      console.error('検索エラー:', error);
      setResults({ videos: [], totalCount: 0 });
    } finally {
      setIsLoading(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.keyword, state.category, state.dateRange, state.sortBy]);

  /// 検索状態変更時の自動検索
  useEffect(() => {
    executeSearch();
  }, [executeSearch]);

  /// フィルタモーダルの表示切替
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  /// 検索結果の要約テキスト
  const getResultsSummary = () => {
    if (isLoading) return '検索中...';
    if (results.totalCount === 0) return '検索結果がありません';
    
    let summary = `${results.totalCount}件の結果`;
    if (state.keyword) {
      summary += ` "${state.keyword}" の検索結果`;
    }
    
    const categoryLabels = {
      all: 'すべて',
      videos: '動画'
    };
    
    if (state.category !== 'all') {
      summary += ` (${categoryLabels[state.category]})`;
    }
    
    return summary;
  };

  const sortOptions: { value: SortBy; label: string }[] = [
    { value: 'date_desc', label: '新しい順' },
    { value: 'date_asc', label: '古い順' },
    { value: 'views_desc', label: '再生数が多い順' },
    { value: 'views_asc', label: '再生数が少ない順' },
    { value: 'likes_desc', label: 'いいねが多い順' },
    { value: 'likes_asc', label: 'いいねが少ない順' },
  ];

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: 'SET_SORT_BY', payload: e.target.value as SortBy });
  };

  return (
    <div className="search-page">
      <div className="search-page__container">
        {/* ヘッダー */}
        <header className="search-page__header">
          <div className="search-page__header-content">
            <h1 className="search-page__title">
              {FiSearch({ className: "search-page__title-icon" })}
              検索
            </h1>
            <p className="search-page__description">
              動画を検索して、お気に入りのコンテンツを見つけよう
            </p>
          </div>
        </header>

        {/* メインコンテンツ */}
        <div className="search-page__main">
          {/* サイドバー（デスクトップ） */}
          <aside className="search-page__sidebar desktop-only">
            <SearchFilters />
          </aside>

          {/* 結果エリア */}
          <div className="search-page__results">
            {/* ツールバー */}
            <div className="search-page__toolbar">
              <div className="search-page__toolbar-left">
                <span className="search-page__results-count">
                  {getResultsSummary()}
                </span>
              </div>
              
              <div className="search-page__toolbar-right">
                {/* ソート機能 */}
                <div className="search-page__sort-container">
                  <select 
                    className="search-page__sort-select"
                    value={state.sortBy}
                    onChange={handleSortChange}
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {FiChevronDown({ className: "search-page__sort-icon" })}
                </div>

                {/* フィルタボタン（モバイル） */}
                <button
                  className="search-page__filter-btn mobile-only"
                  onClick={toggleFilters}
                  aria-label="フィルタを表示"
                >
                  {FiFilter({})}
                  フィルタ
                </button>
              </div>
            </div>

            {/* 検索結果 */}
            <div className={`search-page__content search-page__content--grid`}>
              {isLoading ? (
                <div className="search-page__loading">
                  <div className="search-page__loading-spinner" />
                  <p>検索中...</p>
                </div>
              ) : results.totalCount === 0 ? (
                <div className="search-page__empty">
                  <div className="search-page__empty-content">
                    {FiSearch({ className: "search-page__empty-icon" })}
                    <h3 className="search-page__empty-title">検索結果がありません</h3>
                    <p className="search-page__empty-description">
                      {state.keyword ? (
                        <>
                          「{state.keyword}」に一致するコンテンツが見つかりませんでした。
                          <br />
                          別のキーワードで検索してみてください。
                        </>
                      ) : (
                        <>
                          キーワードを入力して検索を開始してください。
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* 動画結果 */}
                  {results.videos.length > 0 && (
                    <section className="search-page__section">
                      <h2 className="search-page__section-title">
                        動画 ({results.videos.length}件)
                      </h2>
                      <div className={`search-page__grid search-page__grid--grid`}>
                        {results.videos.map((video) => (
                          <div key={video.id} className="search-page__item">
                            <VideoCard video={video} />
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* モバイル用フィルタモーダル */}
      {showFilters && (
        <>
          <div 
            className="search-page__filter-overlay"
            onClick={toggleFilters}
          />
          <SearchFilters
            isModal={true}
            onClose={toggleFilters}
            onSearch={executeSearch}
          />
        </>
      )}
    </div>
  );
};

export default SearchPage; 